import React from 'react';
import { InputNumber, Select, Slider, Typography } from 'antd';
import { ClockCircleOutlined, SettingOutlined, ThunderboltOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import type { SettingsController } from '../../hooks/useSettings';
import { CardHint, Field, SaveButton, SettingsCard, CONTROL_WIDTH } from './parts';

const { Text } = Typography;

const intervalOptions = [
  { value: 30, label: '30 seconds' },
  { value: 60, label: '1 minute' },
  { value: 300, label: '5 minutes' },
  { value: 600, label: '10 minutes' },
  { value: 1800, label: '30 minutes' },
];

export const CheckIntervalCard: React.FC<{ s: SettingsController }> = ({ s }) => (
  <SettingsCard
    title="Check Interval"
    icon={<ClockCircleOutlined />}
    loading={s.loading}
    extra={<SaveButton onClick={s.handleSave} loading={s.saving} />}
  >
    <Field label="Probe every" hint="How often the checker automatically probes all servers">
      <Select
        size="small"
        value={s.checkInterval}
        onChange={s.setCheckInterval}
        options={intervalOptions}
        style={{ width: CONTROL_WIDTH }}
      />
    </Field>
  </SettingsCard>
);

export const CheckDistributionCard: React.FC<{ s: SettingsController }> = ({ s }) => (
  <SettingsCard
    title="Check Distribution"
    icon={<SettingOutlined />}
    loading={s.loading}
    extra={<SaveButton onClick={s.handleSaveDistribution} loading={s.savingDistribution} />}
  >
    <CardHint>
      Balance between network IP checks and proxy checks per scheduled cycle.
      MainIP checks always run. Manual checks are not affected.
    </CardHint>
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Text style={{ fontSize: 13 }}>Network IPs: {s.networkCheckPercent}%</Text>
      <Text style={{ fontSize: 13 }}>Proxies: {100 - s.networkCheckPercent}%</Text>
    </div>
    <Slider
      value={s.networkCheckPercent}
      onChange={s.setNetworkCheckPercent}
      min={0}
      max={100}
      step={5}
      style={{ margin: '4px 4px 0' }}
      tooltip={{ formatter: (val) => `Network: ${val}% / Proxy: ${100 - (val ?? 50)}%` }}
    />
  </SettingsCard>
);

export const ConcurrencyCard: React.FC<{ s: SettingsController }> = ({ s }) => (
  <SettingsCard
    title="Concurrent Checks"
    icon={<ThunderboltOutlined />}
    loading={s.loading}
    extra={<SaveButton onClick={s.handleSaveConcurrency} loading={s.savingConcurrency} />}
  >
    <CardHint>
      Total number of checks running simultaneously across all workers. This value is automatically
      balanced between connected workers. Higher values speed up large scans but use more resources.
    </CardHint>
    <Field label="Checks in flight, fleet-wide" hint="Range: 5 — 500. Default: 100. Automatically distributed across all connected workers.">
      <InputNumber
        size="small"
        value={s.maxConcurrentChecks}
        onChange={(val) => s.setMaxConcurrentChecks(val ?? 50)}
        min={5}
        max={500}
        step={5}
        style={{ width: CONTROL_WIDTH }}
      />
    </Field>
    <Field
      label="Max checks per server"
      hint="Maximum simultaneous checks against the same server. Prevents overloading a single target when the worker pool is larger than the server count. Range: 1 — 10. Default: 1."
    >
      <InputNumber
        size="small"
        value={s.maxChecksPerServer}
        onChange={(val) => s.setMaxChecksPerServer(val ?? 1)}
        min={1}
        max={10}
        step={1}
        style={{ width: CONTROL_WIDTH }}
      />
    </Field>
  </SettingsCard>
);

export const StaleTargetsCard: React.FC<{ s: SettingsController }> = ({ s }) => (
  <SettingsCard
    title="Stale Target Detection"
    icon={<ExclamationCircleOutlined />}
    loading={s.loading}
    extra={<SaveButton onClick={s.handleSaveStale} loading={s.savingStale} />}
  >
    <CardHint>
      Targets (IPs and proxies) that stay DOWN/ERROR continuously beyond this threshold are
      reclassified as STALE: they are shown in a separate grey band in reports and probed at 1/6 the
      normal cadence so chronically-dead modems and abandoned proxies stop burning external API
      budget. Recovery is instant — the next successful check clears the STALE flag automatically.
    </CardHint>
    <Field
      label="Threshold"
      hint="Hours of continuous failure before a target is marked STALE. Pick 72h for the most lenient classification; STALE detection cannot be fully disabled."
    >
      <Select
        size="small"
        value={s.staleThresholdHours}
        onChange={s.setStaleThresholdHours}
        style={{ width: CONTROL_WIDTH + 40 }}
        options={[
          { value: 12, label: '12 hours (aggressive)' },
          { value: 24, label: '24 hours (recommended)' },
          { value: 48, label: '48 hours' },
          { value: 72, label: '72 hours (lenient)' },
        ]}
      />
    </Field>
  </SettingsCard>
);
